import dayjs from 'dayjs';
import { formatQuoteTimeWithZone } from 'utils/quote-time';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import z from 'zod';
import { store } from 'store';
import {
  isBagProductCategory,
  isBkcProductCategory,
} from './product-list/form-items/authentication-config';
import { TWILLY_MAX_QUANTITY } from './twilly';
import { getShopConfig, SHOP_MAP } from 'constant/shop';
import {
  needsManualTaxRegimeSelection,
  ProductTaxRegime,
  SupplierType,
} from 'constants/france-tax';
import { OMS_RECYCLE_ORDER_TYPE_MAP } from 'commons/options';

export const PRODUCT_REMARK_DEFAULT_VALUES = {
  colorRemark: '',
  materialRemark: '',
  hardwareRemark: '',
  stampRemark: '',
};

/** 报价日期默认当前分钟，带本地时区偏移 */
export const getDefaultQuoteTime = () => formatQuoteTimeWithZone(dayjs());

export const createRecyclingConsignmentOrderV2Schema = z
  .object({
    memberSearchValue: z.string(),
    memberId: z.number(),
    email: z.string().nonempty(),
    name: z.string().nonempty(),
    postCode: z.string(),
    postCountryCode: z.string().nonempty(),
    postPhone: z.string().nonempty(),

    postCountryCode2: z.string(),
    postPhone2: z.string(),

    country: z.string().nonempty(),
    city: z.string(),
    detailAddress: z.string(),

    type: z.number(), // 1: 寄卖合同单, 2: 回收合同单
    staffName: z.string().nonempty(), // 担当者姓名
    receptionistId: z.number().optional(), // 受付担当ID
    storeId: z.number(), // 店铺ID
    baseShopRemark: z.string().optional(), // 基础信息备注（客服备注）
    // 法国店供应商类型；法国店必选，其他店铺可空
    // 不用 z.enum/nativeEnum：TS 4.9 无法解析 Zod4 的 const 泛型签名
    supplierType: z
      .custom<SupplierType>(
        (val) =>
          val === SupplierType.COMPANY || val === SupplierType.INDIVIDUAL,
      )
      .optional()
      .nullable(),
    // 法国店商品税制；回收+法人时必选手选，其余场景由 resolve 自动判定后写入或不填
    taxRegime: z
      .custom<ProductTaxRegime>(
        (val) =>
          val === ProductTaxRegime.STANDARD_TVA ||
          val === ProductTaxRegime.MARGIN,
      )
      .optional()
      .nullable(),

    settlementType: z.number(), // 1: 现金结算, 2: 财务打款
    memberPaymentAccountType: z.number().optional(), // 账户类型
    memberPaymentAccountSubType: z.number().optional(), // 账户子类型
    isTradeIn: z.number(), // 是否置换：1-是，0-否

    /**
     * 日本账户
     */
    memberPaymentBankName: z.string().optional(), // 银行名称
    memberPaymentBankAddress: z.string().optional(), // 银行地址
    memberPaymentAccountNo: z.string().optional(), // 账户号码
    memberPaymentAccountName: z.string().optional(), // 账户人姓名
    memberPaymentKiGo: z.string().optional(), // 记号
    memberPaymentBangGo: z.string().optional(), // 番号

    /**
     * 海外账户
     */
    memberPaymentBankCountry: z.string().optional(), // 银行国家
    memberPaymentSwiftCode: z.string().optional(), // SWIFT Code
    memberPaymentAccountAddress: z.string().optional(), // 账户人地址

    /**
     * 香港账户
     */
    memberPaymentBankNo: z.string().optional(), // 银行编号
    memberPaymentFpsNo: z.string().optional(), // FPS账号
    memberPaymentFpsAccountName: z.string().optional(), // FPS账户人名字

    /**
     * 新加坡账户
     */
    memberPaymentPayNowNo: z.string().optional(), // PayNow账号
    memberPaymentPayNowAccountName: z.string().optional(), // PayNow账户人名字
    memberPaymentBankPhone: z.string().optional(), // 银行预留电话

    payoutDetail: z.string().optional(),

    productList: z.array(
      z.object({
        id: z.string(),
        productCategoryIdList: z.array(z.number()).nonempty(),
        brandName: z.string(),
        quantity: z.number().int().min(1).max(TWILLY_MAX_QUANTITY).optional(),
        name: z.object({
          en: z.string().nonempty(),
          ja: z.string().nonempty(),
          zh_CN: z.string().nonempty(),
          zh_TW: z.string().nonempty(),
        }),
        rank: z.string().nonempty(),
        rankDesc: z.object({
          en: z.string().nonempty(),
          ja: z.string().nonempty(),
          zh_CN: z.string().nonempty(),
          zh_TW: z.string().nonempty(),
        }),
        attrSize: z.object({
          en: z.string(),
          ja: z.string(),
          zh_CN: z.string(),
          zh_TW: z.string(),
        }),

        /**
         * 等级描述，当前选中的快捷选项，不需要上传后端，要存到表单里，主要是需要在 rank 组件中访问
         */
        selectedRankDescOptions: z.record(z.string(), z.array(z.number())),

        color: z.array(z.string().nonempty()),
        colors: z.string(),
        colorRemark: z.string().optional(),

        material: z.array(z.array(z.string().nonempty())),
        materialRemark: z.string().optional(),
        hardware: z.array(z.string().nonempty()),
        hardwareRemark: z.string().optional(),
        stamp: z.string(),
        stampRemark: z.string().optional(),
        fullStamp: z.string(),
        accessory: z.array(z.string().nonempty()),

        // 最终寄卖价格，只有当订单类型为寄卖合同单时，才需要填写
        finalSalePrice: z.number().optional().nullable(),
        // 最终回收价格，只有当订单类型为回收合同单时，才需要填写
        finalRecyclePrice: z.number().optional().nullable(),
        // 商品售价（非必填）
        price: z.number().optional().nullable(),
        // 报价担当，记录员工 id
        quoteStaffId: z.number().optional(),
        // 报价日期，精确到分钟，带时区偏移，例如 2026-10-26T16:03:00+09:00
        quoteTime: z.string().optional().nullable(),

        firstValuationShopRemark: z.string(),
        guestRemarks: z.string(),
        firstValuationStock: z.number(), // 1: 无库存, 2: 有库存
        firstValuationStockRemark: z.string(),

        productPics: z.string().nonempty(),
        storeReceiptPics: z.array(z.string().nonempty()),
        aiIdentifyPics: z.array(z.string().nonempty()),
        authenticationBagId: z.number().optional(),
        isFakeBag: z.boolean().optional(),
        fakeBagReason: z.string().optional(),
        // 复核确认人，二次确认检查用，包类必选
        reviewerName: z.string().optional(),
        initXmAssets: z.boolean().optional(),

        facebookEnabled: z.number().optional(),

        // 包型名称，用于判断是否是 BKC（Birkin/Kelly/Constance）
        bagTypeName: z.string().optional(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    if (
      data.storeId === SHOP_MAP.FRANCE &&
      (data.supplierType === undefined || data.supplierType === null)
    ) {
      ctx.addIssue({
        path: ['supplierType'],
        code: 'custom',
        message: i18n.t(LOCALS.please_select) || '请选择供应商类型',
      });
    }

    // 法国店：回收 + 法人时需手动选择税制（标准 TVA / 差额税）
    if (
      data.storeId === SHOP_MAP.FRANCE &&
      needsManualTaxRegimeSelection(data.type, data.supplierType) &&
      (data.taxRegime === undefined || data.taxRegime === null)
    ) {
      ctx.addIssue({
        path: ['taxRegime'],
        code: 'custom',
        message: i18n.t(LOCALS.please_select) || '请选择商品税制',
      });
    }

    data.productList.forEach((item, index) => {
      if (
        data.type === OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT &&
        item.isFakeBag !== true &&
        (item.finalSalePrice === undefined || item.finalSalePrice === null)
      ) {
        ctx.addIssue({
          path: ['productList', index, 'finalSalePrice'],
          code: 'custom',
          message: '寄卖价格不能为空',
        });
      }

      if (
        data.type === OMS_RECYCLE_ORDER_TYPE_MAP.RECYCLE &&
        item.isFakeBag !== true &&
        (item.finalRecyclePrice === undefined ||
          item.finalRecyclePrice === null)
      ) {
        ctx.addIssue({
          path: ['productList', index, 'finalRecyclePrice'],
          code: 'custom',
          message: '回收价格不能为空',
        });
      }

      // 一级分类 value === 'bags' 才视为包类，人工复核必填
      const { productCategoryCascaderOptions } = store.getState().globalInfo;
      if (
        isBagProductCategory(
          item.productCategoryIdList,
          productCategoryCascaderOptions,
        )
      ) {
        if (typeof item.isFakeBag !== 'boolean') {
          ctx.addIssue({
            path: ['productList', index, 'isFakeBag'],
            code: 'custom',
            message:
              i18n.t(LOCALS.select_real_or_fake_bag) || '请选择真包或假包',
          });
        }

        // 判定为假包时，判断原因必填
        if (item.isFakeBag === true && !item.fakeBagReason?.trim()) {
          ctx.addIssue({
            path: ['productList', index, 'fakeBagReason'],
            code: 'custom',
            message: i18n.t(LOCALS.please_enter) || '请填写拒绝原因',
          });
        }

        // 复核确认人必选
        if (!item.reviewerName) {
          ctx.addIssue({
            path: ['productList', index, 'reviewerName'],
            code: 'custom',
            message: i18n.t(LOCALS.please_select) || '请选择',
          });
        }
      }

      // BKC 包型必填验证：二级分类 value 属于 birkin/kelly/constance
      const isBKC = isBkcProductCategory(
        item.productCategoryIdList,
        productCategoryCascaderOptions,
      );
      if (isBKC) {
        if (!item.accessory || item.accessory.length === 0) {
          ctx.addIssue({
            path: ['productList', index, 'accessory'],
            code: 'custom',
            message: i18n.t(LOCALS.bkc_accessory_required) || 'BKC配件不能为空',
          });
        }
      }
    });
  });

export type CreateRecyclingConsignmentOrderV2SchemaType = z.infer<
  typeof createRecyclingConsignmentOrderV2Schema
>;

export const getDefaultCountryCodeByStoreId = (storeId: number) => {
  return getShopConfig(storeId).countryCode;
};

export const recycleConsignmentStoreList = [
  {
    value: SHOP_MAP.GINZA,
    label: i18n.t(LOCALS.ginza_shop),
  },
  {
    value: SHOP_MAP.HONGKONG,
    label: i18n.t(LOCALS.hongkong_shop),
  },
  {
    value: SHOP_MAP.SINGAPORE,
    label: i18n.t(LOCALS.singapore_shop),
  },
  {
    value: SHOP_MAP.SINGAPORE_ASU_GST,
    label: i18n.t(LOCALS.singapore_shop_asu_gst),
  },
  {
    value: SHOP_MAP.SINGAPORE_GX,
    label: i18n.t(LOCALS.kkIwEnEVml),
  },
  {
    value: SHOP_MAP.SHANG_HAI,
    label: i18n.t(LOCALS.shanghai_store),
  },
  {
    value: SHOP_MAP.UMEDA,
    label: i18n.t(LOCALS.umeda_store),
  },
  {
    value: SHOP_MAP.DUBAI,
    label: i18n.t(LOCALS.dubai_store),
  },
  {
    value: SHOP_MAP.FRANCE,
    label: i18n.t(LOCALS.france_store),
  },
  {
    value: SHOP_MAP.NEW_YORK,
    label: i18n.t(LOCALS.new_york_store),
  },
];

export const FORM_KEY = 'recyclingFormCache';
